import { type RefObject, useMemo } from 'react'
import { FloatingInput } from '@/components/commons/floating-input'
import {
	Combobox,
	ComboboxComponents,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList
} from '@/components/ui/combobox'
import { ChevronDownIcon, Lock03Icon } from '@/components/ui/icons'
import { Skeleton } from '@/components/ui/skeleton'
import type { ComboboxOption } from '@/features/marquee/components/combobox-field'

const SKELETON_ROWS = ['s1', 's2', 's3']

// Combining diacritical marks (U+0300–U+036F), what NFD splits accents into.
const DIACRITICS_REGEX = /[\u0300-\u036f]/g

// Lowercase and strip accents so 'sao tome' matches 'São Tomé'
const normalizeForSearch = (text: string) =>
	text.normalize('NFD').replace(DIACRITICS_REGEX, '').toLowerCase()

const matchesSearch = (item: ComboboxOption, query: string) =>
	normalizeForSearch(item.label).includes(normalizeForSearch(query.trim()))

interface FloatingComboboxFieldProps {
	options: ComboboxOption[]
	value: string | null
	onChange: (value: string | null) => void
	/** Sits inside the box and floats up once a value is picked. */
	label: string
	searchPlaceholder: string
	emptyLabel: string
	errorMessage?: string
	helpText?: string
	/** Greyed out with a lock in place of the chevron. */
	disabled?: boolean
	/**
	 * The options are still loading: the field stays usable, and an open popup
	 * shows skeleton rows instead of the list (or a misleading "no results").
	 */
	loading?: boolean
	/**
	 * Where the popup is portalled. Inside a dialog or drawer it has to be the
	 * modal itself: both block pointer events outside their own subtree.
	 */
	container?: RefObject<HTMLElement | null>
}

/**
 * Single-select combobox in the `FloatingInput` look, for the dialogs built on
 * it (Locations). The field box itself is the trigger, through `rootProps`;
 * typing happens in the popup's search input, so the box stays read-only.
 */
export function FloatingComboboxField({
	options,
	value,
	onChange,
	label,
	searchPlaceholder,
	emptyLabel,
	errorMessage,
	helpText,
	disabled,
	loading,
	container
}: FloatingComboboxFieldProps) {
	const selectedOption = useMemo(
		() => options.find((option) => option.value === value) ?? null,
		[options, value]
	)
	const isDisabled = disabled

	const endSlot = disabled ? (
		<Lock03Icon className='shrink-0' size={20} />
	) : (
		<ComboboxComponents.Icon className='shrink-0 text-grey-1000'>
			<ChevronDownIcon
				className='in-data-popup-open:rotate-180 transition-transform duration-150'
				size={20}
			/>
		</ComboboxComponents.Icon>
	)

	return (
		<Combobox
			disabled={isDisabled}
			filter={matchesSearch}
			items={options}
			onValueChange={(next) => onChange(next ? next.value : null)}
			value={selectedOption}
		>
			<ComboboxComponents.Trigger
				disabled={isDisabled}
				nativeButton={false}
				render={(props) => (
					<FloatingInput
						disabled={isDisabled}
						endSlot={endSlot}
						errorMessage={errorMessage}
						helpText={helpText}
						inputSize='lg'
						label={label}
						readOnly
						rootProps={{
							...props,
							// The box is a trigger, not a read-only value:
							// it keeps the editable look.
							'data-read-only': undefined,
							className: 'cursor-pointer [&_input]:cursor-pointer'
						}}
						tabIndex={-1}
						type='text'
						value={selectedOption?.label ?? ''}
					/>
				)}
			/>
			<ComboboxContent
				className='w-(--anchor-width)'
				container={container}
			>
				<ComboboxInput placeholder={searchPlaceholder} />
				{loading ? (
					<div className='flex flex-col gap-3 p-3'>
						{SKELETON_ROWS.map((key) => (
							<Skeleton height={20} key={key} />
						))}
					</div>
				) : (
					<>
						<ComboboxList>
							{(item: ComboboxOption) => (
								<ComboboxItem key={item.value} value={item}>
									{item.label}
								</ComboboxItem>
							)}
						</ComboboxList>
						<ComboboxEmpty>{emptyLabel}</ComboboxEmpty>
					</>
				)}
			</ComboboxContent>
		</Combobox>
	)
}
